import { supabase } from './supabase';

// Invoicing helpers (Admin → Invoices, public /i/:token). Money is integer cents (USD).
// computeInvoice() is the single place totals and taxes are worked out; the editor stores its
// result on the invoice and lines, the PDF and reports read those stored numbers.

export const money = (cents) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format((cents || 0) / 100);
export const toCents = (v) => {
  const n = Number(String(v ?? '').replace(/[$,\s]/g, ''));
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
};
export const centsToInput = (c) => (c ? (c / 100).toFixed(2) : '');
export const longDate = (d) => (d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '');
export const shortDate = (d) => (d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');
export const isoToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
export const addDays = (iso, days) => {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + Number(days || 0));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
export const fmtQty = (q) => {
  const n = Number(q || 0);
  return Number.isInteger(n) ? String(n) : n.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
};
export const UNITS = ['each', 'month', 'year', 'seat', 'hour', 'card'];
export const METHODS = { check: 'Check', card: 'Card', ach: 'Bank transfer', cash: 'Cash', other: 'Other' };

/**
 * Totals for a set of lines. lines: [{quantity, unit_price_cents, taxable, tax_rate_ids[]}]
 * taxRates: {id: {id, name, rate}}; exempt: client is tax exempt.
 * The invoice discount is spread over the lines in proportion to their amount, and tax is
 * charged on each taxable line after its share of the discount.
 */
export function computeInvoice({ lines, discountType, discountValue, taxRates, exempt }) {
  const amounts = lines.map((l) => Math.round(Number(l.quantity || 0) * Number(l.unit_price_cents || 0)));
  const subtotal = amounts.reduce((a, b) => a + b, 0);
  let discount = 0;
  if (discountType === 'percent') discount = Math.round(subtotal * Math.min(100, Number(discountValue || 0)) / 100);
  if (discountType === 'amount') discount = Math.min(subtotal, Math.round(Number(discountValue || 0) * 100));
  let tax = 0;
  const out = lines.map((l, i) => {
    const share = subtotal ? Math.round((discount * amounts[i]) / subtotal) : 0;
    const base = amounts[i] - share;
    const taxes = !exempt && l.taxable
      ? (l.tax_rate_ids || []).map((id) => taxRates[id]).filter(Boolean).map((r) => ({
          tax_rate_id: r.id, name: r.name, rate: Number(r.rate), cents: Math.round((base * Number(r.rate)) / 100),
        }))
      : [];
    const lineTax = taxes.reduce((a, t) => a + t.cents, 0);
    tax += lineTax;
    return { ...l, amount_cents: amounts[i], tax_cents: lineTax, taxes };
  });
  return { lines: out, subtotal, discount, tax, total: subtotal - discount + tax };
}

/** Tax totals by rate name, e.g. [{name: 'Florida sales tax', rate: 6, cents: 600}]. */
export function taxBreakdown(lines) {
  const by = {};
  for (const l of lines || []) {
    for (const t of l.taxes || []) {
      const key = `${t.name}|${t.rate}`;
      by[key] = by[key] || { name: t.name, rate: Number(t.rate), cents: 0 };
      by[key].cents += t.cents;
    }
  }
  return Object.values(by);
}

/** Status for lists and the invoice page; overdue = sent/partial and past due. */
export function invoiceStatus(inv) {
  const overdue = ['sent', 'partial'].includes(inv.status) && inv.due_date < isoToday();
  if (inv.status === 'draft') return { label: 'Draft', tone: 'muted' };
  if (inv.status === 'void') return { label: 'Void', tone: 'muted' };
  if (inv.status === 'paid') return { label: 'Paid', tone: 'good' };
  if (overdue) return { label: inv.status === 'partial' ? 'Overdue · partly paid' : 'Overdue', tone: 'bad' };
  if (inv.status === 'partial') return { label: 'Partly paid', tone: 'warn' };
  return { label: inv.viewed_at ? 'Sent · viewed' : 'Sent', tone: 'info' };
}
export const STATUS_TONES = {
  muted: 'bg-surface text-ink-muted border border-line',
  good: 'bg-blue text-white',
  bad: 'bg-coral-soft text-coral-ink',
  warn: 'bg-coral-soft text-ink',
  info: 'bg-blue-soft text-blue',
};

/* ------------------------------------------------------------------ data */

const unwrap = ({ data, error }) => {
  if (error) throw new Error(error.message);
  return data;
};

export async function loadSeller() {
  const rows = unwrap(await supabase.from('sellers').select('*').order('created_at').limit(1));
  return rows[0] || null;
}

export const loadTaxRates = (sellerId) =>
  supabase.from('tax_rates').select('*').eq('seller_id', sellerId).order('created_at').then(unwrap);
export const loadClients = (sellerId) =>
  supabase.from('invoice_clients').select('*, organizations(name)').eq('seller_id', sellerId).order('name').then(unwrap);
export const loadProducts = (sellerId) =>
  supabase.from('invoice_products').select('*').eq('seller_id', sellerId).order('name').then(unwrap);
export const loadInvoices = (sellerId) =>
  supabase.from('invoices')
    .select('id, number, status, issue_date, due_date, total_cents, paid_cents, tax_cents, subtotal_cents, discount_cents, sent_at, viewed_at, paid_at, voided_at, client_id, invoice_clients(name)')
    .eq('seller_id', sellerId).order('created_at', { ascending: false }).then(unwrap);

export async function loadInvoice(id) {
  const [inv, lines, payments, events] = await Promise.all([
    supabase.from('invoices').select('*, invoice_clients(*)').eq('id', id).single().then(unwrap),
    supabase.from('invoice_lines').select('*').eq('invoice_id', id).order('position').then(unwrap),
    supabase.from('invoice_payments').select('*').eq('invoice_id', id).order('received_on').then(unwrap),
    supabase.from('invoice_events').select('*').eq('invoice_id', id).order('created_at').then(unwrap),
  ]);
  return { inv, lines, payments, events };
}

/** Save a draft: the invoice row and all its lines (replaced). Returns the invoice id. */
export async function saveDraft(invoice, lines) {
  const { id, ...fields } = invoice;
  let invoiceId = id;
  if (invoiceId) {
    unwrap(await supabase.from('invoices').update(fields).eq('id', invoiceId).select('id').single());
    unwrap(await supabase.from('invoice_lines').delete().eq('invoice_id', invoiceId));
  } else {
    invoiceId = unwrap(await supabase.from('invoices').insert(fields).select('id').single()).id;
  }
  if (lines.length) {
    unwrap(await supabase.from('invoice_lines').insert(lines.map((l, i) => ({
      invoice_id: invoiceId, position: i, product_id: l.product_id || null, description: l.description || '',
      quantity: Number(l.quantity || 0), unit: l.unit || null, unit_price_cents: l.unit_price_cents || 0,
      taxable: !!l.taxable, product_line: l.product_line || null, taxes: l.taxes || [],
      amount_cents: l.amount_cents || 0, tax_cents: l.tax_cents || 0,
    }))));
  }
  return invoiceId;
}

export async function invoiceCall(action, payload) {
  const { data, error } = await supabase.functions.invoke('invoices', { body: { action, ...payload } });
  if (error) {
    let message = error.message;
    try {
      const body = await error.context?.json?.();
      if (body?.error) message = body.error;
    } catch { /* keep generic */ }
    throw new Error(message);
  }
  return data;
}

/** base64 for a Uint8Array (PDF bytes), in chunks so large files don't overflow the stack. */
export function toBase64(bytes) {
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

/** The printable shape of an invoice (sent: frozen snapshots; draft: current seller/client). */
export function invoiceDocument({ inv, lines, seller, client }) {
  const s = inv.seller_snapshot || (seller && {
    name: seller.name, email: seller.email, phone: seller.phone, website: seller.website, address: seller.address,
    ein: seller.show_ein ? seller.ein : null, logo_url: seller.logo_url,
    check_payable_to: seller.check_payable_to || seller.name, remit_address: seller.remit_address || seller.address,
    footer: seller.invoice_footer,
  }) || {};
  const c = inv.client_snapshot || (client && {
    name: client.name, contact_name: client.contact_name, emails: client.emails, phone: client.phone,
    address: client.address, tax_exempt: client.tax_exempt, tax_exempt_id: client.tax_exempt_id,
  }) || {};
  return {
    seller: s, client: c, number: inv.number, status: inv.status, issue_date: inv.issue_date, due_date: inv.due_date,
    po_number: inv.po_number, notes: inv.notes, terms: inv.terms, lines,
    subtotal_cents: inv.subtotal_cents, discount_cents: inv.discount_cents, tax_cents: inv.tax_cents,
    total_cents: inv.total_cents, paid_cents: inv.paid_cents || 0,
  };
}

/* --------------------------------------------------------------- reports */

const ageDays = (due) => Math.floor((new Date(`${isoToday()}T12:00:00`) - new Date(`${due}T12:00:00`)) / 86400000);

/** Money still owed, by client and by how late it is. */
export function receivablesAging(invoices) {
  const buckets = ['Current', '1–30 days', '31–60 days', '61–90 days', '90+ days'];
  const rows = {};
  const totals = buckets.map(() => 0);
  for (const inv of invoices) {
    if (!['sent', 'partial'].includes(inv.status)) continue;
    const due = inv.total_cents - inv.paid_cents;
    if (due <= 0) continue;
    const d = ageDays(inv.due_date);
    const b = d <= 0 ? 0 : d <= 30 ? 1 : d <= 60 ? 2 : d <= 90 ? 3 : 4;
    const name = inv.invoice_clients?.name || 'Unknown client';
    rows[name] = rows[name] || { name, cells: buckets.map(() => 0), total: 0 };
    rows[name].cells[b] += due;
    rows[name].total += due;
    totals[b] += due;
  }
  return { buckets, rows: Object.values(rows).sort((a, b) => b.total - a.total), totals, total: totals.reduce((a, b) => a + b, 0) };
}

/**
 * Sales tax for a period. basis 'accrual': invoices issued in the period (not void).
 * basis 'cash': payments received in the period, each payment's tax share in proportion
 * to the invoice's tax / total.
 */
export async function salesTaxReport(sellerId, from, to, basis) {
  const invoices = unwrap(await supabase.from('invoices')
    .select('id, number, status, issue_date, subtotal_cents, discount_cents, tax_cents, total_cents, client_snapshot, invoice_lines(taxable, amount_cents, taxes, product_line)')
    .eq('seller_id', sellerId).neq('status', 'draft').neq('status', 'void'));
  const byId = Object.fromEntries(invoices.map((i) => [i.id, i]));
  const rates = {};
  let taxable = 0;
  let exempt = 0;
  let tax = 0;
  const add = (inv, factor) => {
    const discRatio = inv.subtotal_cents ? (inv.subtotal_cents - inv.discount_cents) / inv.subtotal_cents : 1;
    for (const l of inv.invoice_lines) {
      const base = Math.round(l.amount_cents * discRatio * factor);
      if (l.taxable && !inv.client_snapshot?.tax_exempt && (l.taxes || []).length) taxable += base; else exempt += base;
      for (const t of l.taxes || []) {
        const key = `${t.name}|${t.rate}`;
        rates[key] = rates[key] || { name: t.name, rate: Number(t.rate), cents: 0 };
        const cents = Math.round(t.cents * factor);
        rates[key].cents += cents;
        tax += cents;
      }
    }
  };
  if (basis === 'cash') {
    const payments = unwrap(await supabase.from('invoice_payments').select('invoice_id, amount_cents, received_on')
      .gte('received_on', from).lte('received_on', to));
    for (const p of payments) {
      const inv = byId[p.invoice_id];
      if (inv && inv.total_cents > 0) add(inv, p.amount_cents / inv.total_cents);
    }
  } else {
    for (const inv of invoices) if (inv.issue_date >= from && inv.issue_date <= to) add(inv, 1);
  }
  return { rates: Object.values(rates), taxable, exempt, tax };
}

/** Revenue (before tax, after discount) by product line for invoices issued in a period. */
export function revenueByLine(invoicesWithLines, from, to) {
  const by = {};
  for (const inv of invoicesWithLines) {
    if (inv.status === 'draft' || inv.status === 'void' || inv.issue_date < from || inv.issue_date > to) continue;
    const ratio = inv.subtotal_cents ? (inv.subtotal_cents - inv.discount_cents) / inv.subtotal_cents : 1;
    for (const l of inv.invoice_lines || []) {
      const key = l.product_line || 'Other';
      by[key] = (by[key] || 0) + Math.round(l.amount_cents * ratio);
    }
  }
  return Object.entries(by).map(([name, cents]) => ({ name, cents })).sort((a, b) => b.cents - a.cents);
}
